<template>
  <div class="risk-dashboard">
    <div class="m-16"></div>
    
    <!-- Export Button -->
    <div class="export-container">
      <div class="button-group">
        <Import @open-import-wizard="riskStore.openImportWizard" />
        <Export @export-png="exportRiskMapsAsZip" />
      </div>
    </div>
    
    <!-- Import Wizard Modal -->
    <ImportWizard 
      :is-open="riskStore.isImportWizardOpen" 
      @close="riskStore.closeImportWizard" 
      :on-import="handleImportRisks" 
    />
    
    <div class="dashboard-container">
      
      <!-- Risk Matrices Section -->
      <div class="matrices-container" >
        <!-- Left Section: Original Risk Matrix -->
        <div class="risk-section" ref="riskMatrix">
          <h2 class="section-title">Risk Matrix</h2>
          
          <!-- Risk Matrix -->
          <div class="risk-matrix">
            <div class="matrix-header">
              <div class="matrix-label">Likelihood</div>
              <div class="matrix-labels">
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
              </div>
            </div>
            
            <div class="matrix-grid">
              <div class="matrix-row" v-for="severity in 4" :key="severity">
                <div class="severity-label">{{ 5 - severity }}</div>
                <div 
                  v-for="likelihood in 4" 
                  :key="likelihood"
                  class="matrix-cell"
                  :class="riskStore.getRiskLevelClass(5 - severity, likelihood)"
                >
                  <div class="risk-markers" v-if="getRiskMarkers(5 - severity, likelihood)">
                    {{ getRiskMarkers(5 - severity, likelihood) }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="matrix-footer">
              <div class="severity-label">Severity</div>
            </div>
          </div>
        </div>
        
        <!-- Right Section: Residual Risk Matrix -->
        <div class="risk-section" ref="residualRiskMatrix">
          <h2 class="section-title">Residual Risk Matrix</h2>
          
          <!-- Risk Matrix -->
          <div class="risk-matrix">
            <div class="matrix-header">
              <div class="matrix-label">Likelihood</div>
              <div class="matrix-labels">
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
              </div>
            </div>
            
            <div class="matrix-grid">
              <div class="matrix-row" v-for="severity in 4" :key="severity">
                <div class="severity-label">{{ 5 - severity }}</div>
                <div 
                  v-for="likelihood in 4" 
                  :key="likelihood"
                  class="matrix-cell"
                  :class="riskStore.getRiskLevelClass(5 - severity, likelihood)"
                >
                  <div class="risk-markers" v-if="getResidualRiskMarkers(5 - severity, likelihood)">
                    {{ getResidualRiskMarkers(5 - severity, likelihood) }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="matrix-footer">
              <div class="severity-label">Severity</div>
            </div>
          </div>
        </div>
      </div>

            <!-- Color Legend -->
      <div class="legend">
        <div class="legend-item">
          <div class="legend-color color-low"></div>
          <span>Low Risk</span>
        </div>
        <div class="legend-item">
          <div class="legend-color color-medium"></div>
          <span>Medium Risk</span>
        </div>
        <div class="legend-item">
          <div class="legend-color color-high"></div>
          <span>High Risk</span>
        </div>
      </div>
      
      <!-- Full Width Risk Table Section -->
      <div class="table-section">
        <div class="table-section-header">
          <h3 class="table-section-title">Risk Details</h3>
          <div class="table-section-drag-handle" title="Drag to resize (coming soon)">⋮⋮</div>
        </div>
        <!-- Risk Table -->
        <div class="risk-table">
          <table>
            <thead>
              <tr>
                <th class="hidden-column">ID</th>
                <th>Index</th>
                <th>Description</th>
                <th>Likelihood</th>
                <th>Severity</th>
                <th>Risk</th>
                <th>Residual Likelihood</th>
                <th>Residual Severity</th>
                <th>Residual Risk</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="risks.length === 0" class="empty-state-row">
                <td :colspan="10" class="empty-state">
                  <div class="empty-state-content">
                    <p>No risks added yet</p>
                    <p class="empty-state-hint">Click "Add a risk" to get started</p>
                  </div>
                </td>
              </tr>
              <tr v-for="risk in risks" :key="risk.id">
                <td class="risk-id hidden-column">R{{ risk.id }}</td>
                <td>
                  <div v-if="isFieldEditing(risk.id, RiskField.Index)" class="editing-cell">
                    <input 
                      v-model="editingValue" 
                      @blur="saveEdit(risk.id, RiskField.Index)"
                      @keyup.enter="saveEdit(risk.id, RiskField.Index)"
                      @keyup.esc="cancelEdit"
                      class="edit-input"
                      type="number"
                      min="1"
                    />
                  </div>
                  <div v-else @click="startEdit(risk.id, RiskField.Index)" class="editable-cell">
                    {{ risk.index }}
                  </div>
                </td>
                <td class="risk-description">
                  <div v-if="isFieldEditing(risk.id, RiskField.Description)" class="editing-cell">
                    <input 
                      v-model="editingValue" 
                      @blur="saveEdit(risk.id, RiskField.Description)"
                      @keyup.enter="saveEdit(risk.id, RiskField.Description)"
                      @keyup.esc="cancelEdit"
                      class="edit-input"
                      type="text"
                    />
                  </div>
                  <div v-else @click="startEdit(risk.id, RiskField.Description)" class="editable-cell">
                    {{ risk.description }}
                  </div>
                </td>
                <td>
                  <div v-if="isFieldEditing(risk.id, RiskField.Likelihood)" class="editing-cell">
                    <select 
                      v-model="editingValue" 
                      @blur="saveEdit(risk.id, RiskField.Likelihood)"
                      @keyup.enter="saveEdit(risk.id, RiskField.Likelihood)"
                      @keyup.esc="cancelEdit"
                      class="edit-select"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                  <div v-else @click="startEdit(risk.id, RiskField.Likelihood)" class="editable-cell">
                    {{ risk.likelihood }}
                  </div>
                </td>
                <td>
                  <div v-if="isFieldEditing(risk.id, RiskField.Severity)" class="editing-cell">
                    <select 
                      v-model="editingValue" 
                      @blur="saveEdit(risk.id, RiskField.Severity)"
                      @keyup.enter="saveEdit(risk.id, RiskField.Severity)"
                      @keyup.esc="cancelEdit"
                      class="edit-select"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                  <div v-else @click="startEdit(risk.id, RiskField.Severity)" class="editable-cell">
                    {{ risk.severity }}
                  </div>
                </td>
                <td>
                  <div class="color-indicator" :class="riskStore.getRiskColorClass(risk.likelihood, risk.severity)"></div>
                </td>
                <td>
                  <div v-if="isFieldEditing(risk.id, RiskField.ResidualLikelihood)" class="editing-cell">
                    <select 
                      v-model="editingValue" 
                      @blur="saveEdit(risk.id, RiskField.ResidualLikelihood)"
                      @keyup.enter="saveEdit(risk.id, RiskField.ResidualLikelihood)"
                      @keyup.esc="cancelEdit"
                      class="edit-select"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                  <div v-else @click="startEdit(risk.id, RiskField.ResidualLikelihood)" class="editable-cell">
                    {{ risk.residualLikelihood }}
                  </div>
                </td>
                <td>
                  <div v-if="isFieldEditing(risk.id, RiskField.ResidualSeverity)" class="editing-cell">
                    <select 
                      v-model="editingValue" 
                      @blur="saveEdit(risk.id, RiskField.ResidualSeverity)"
                      @keyup.enter="saveEdit(risk.id, RiskField.ResidualSeverity)"
                      @keyup.esc="cancelEdit"
                      class="edit-select"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                  <div v-else @click="startEdit(risk.id, RiskField.ResidualSeverity)" class="editable-cell">
                    {{ risk.residualSeverity }}
                  </div>
                </td>
                <td>
                  <div class="color-indicator" :class="riskStore.getRiskColorClass(risk.residualLikelihood, risk.residualSeverity)"></div>
                </td>
                <td>
                  <button class="delete-btn" @click="deleteRisk(risk.id)">
                    🗑️
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          
          <button class="add-risk-btn" @click="addRisk">
            + Add a risk
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, nextTick, computed } from 'vue'
import { useRoute } from 'vue-router'
import { createRiskMapStore } from './stores'
import Export from '../stakeholdersMap/components/Export.vue'
import Import from './components/Import.vue'
import ImportWizard from './components/ImportWizard.vue'
import html2canvas from 'html2canvas'
import JSZip from 'jszip'
import { Risk } from '../../riskMap/types/Risk'
import { RiskField } from '../../riskMap/types/RiskField'
import { riskMapRequest } from './services/RiskMapRequest'

const route = useRoute();
const riskStore = createRiskMapStore(riskMapRequest);

// Reactive state for editing
const editingRisk = computed(() => riskStore.editingRisk)
const editingValue = ref('')

// Refs for matrix sections (export only the matrices, not the table)
const riskMatrix = ref<HTMLElement | null>(null)
const residualRiskMatrix = ref<HTMLElement | null>(null)

const risks = computed(() => riskStore.risks)
const riskMarkers = computed(() => riskStore.riskMarkersMap)
const residualRiskMarkers = computed(() => riskStore.residualRiskMarkersMap)

// Helper function to check if a specific field is being edited for a risk
const isFieldEditing = (riskId: string, field: RiskField): boolean => {
  return editingRisk.value?.id === riskId && editingRisk.value?.field === field
}

// Helper functions to get markers
const getRiskMarkers = (severity: number, likelihood: number) => {
  const key = `${severity}-${likelihood}`
  const markers = riskMarkers.value[key] || []
  return markers.length > 0 ? markers.join(', ') : ''
}

const getResidualRiskMarkers = (severity: number, likelihood: number) => {
  const key = `${severity}-${likelihood}`
  const markers = residualRiskMarkers.value[key] || []
  return markers.length > 0 ? markers.join(', ') : ''
}

// Load initial data when component mounts
onMounted(async () => {
  const mapId = (route.query.id as string);
  if (riskStore.risks.length === 0 || riskStore.currentMapId !== mapId) {
    await riskStore.fetchRiskMap(mapId)
  }
})

// Helper: draw diagonal "GraphRM" watermark on canvas
function addWatermark(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.save();
  ctx.globalAlpha = 0.2;
  ctx.font = 'bold 48px Arial';
  ctx.fillStyle = '#666666';
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  ctx.translate(centerX, centerY);
  ctx.rotate(-Math.PI / 4);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('GraphRM', 0, 0);
  ctx.restore();
}

// Helper function to apply light mode styles for export
function applyLightModeStyles(element: HTMLElement): () => void {
  const elementsToRestore: Array<{ element: HTMLElement; originalStyle: string }> = [];
  
  // Apply light mode to risk-section background
  const riskSection = element as HTMLElement;
  elementsToRestore.push({
    element: riskSection,
    originalStyle: riskSection.style.cssText
  });
  riskSection.style.backgroundColor = '#ffffff';
  
  // Apply black color to matrix-header labels
  const matrixHeaderLabels = element.querySelectorAll('.matrix-header .matrix-label, .matrix-header .matrix-labels span');
  matrixHeaderLabels.forEach((label) => {
    const htmlLabel = label as HTMLElement
    elementsToRestore.push({
      element: htmlLabel,
      originalStyle: htmlLabel.style.cssText
    });
    htmlLabel.style.color = '#000000';
  });
  
  // Apply black color to severity-labels
  const severityLabels = element.querySelectorAll('.severity-label');
  severityLabels.forEach((label) => {
    const htmlLabel = label as HTMLElement;
    elementsToRestore.push({
      element: htmlLabel,
      originalStyle: htmlLabel.style.cssText
    });
    htmlLabel.style.color = '#000000';
  });
  
  // Apply black color to section-title
  const sectionTitle = element.querySelector('.section-title');
  if (sectionTitle) {
    const htmlTitle = sectionTitle as HTMLElement;
    elementsToRestore.push({
      element: htmlTitle,
      originalStyle: htmlTitle.style.cssText
    });
    htmlTitle.style.color = '#000000';
  }
  
  // Return cleanup function to restore original styles
  return () => {
    elementsToRestore.forEach(({ element, originalStyle }) => {
      element.style.cssText = originalStyle;
    });
  };
}

// Returns PNG blob of the risk matrix (light mode + watermark)
async function exportRiskMapAsPNG(): Promise<Blob | null> {
  if (!riskMatrix.value) return null;
  
  // Apply light mode styles
  const restoreStyles = applyLightModeStyles(riskMatrix.value);
  
  try {
    const canvas = await html2canvas(riskMatrix.value, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true
    });
    addWatermark(canvas);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
          restoreStyles();
          resolve(blob ?? null);
      }, 'image/png');
    });
  } catch (error) {
    restoreStyles();
    throw error;
  }
}

// Returns PNG blob of the residual risk matrix (light mode + watermark)
async function exportResidualRiskMapAsPNG(): Promise<Blob | null> {
  if (!residualRiskMatrix.value) return null;
  
  // Apply light mode styles
  const restoreStyles = applyLightModeStyles(residualRiskMatrix.value);
  
  try {
    const canvas = await html2canvas(residualRiskMatrix.value, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true
    });
    addWatermark(canvas);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        restoreStyles();
        resolve(blob ?? null);
      }, 'image/png');
    });
  } catch (error) {
    restoreStyles();
    throw error;
  }
}

// Export both matrices as a zip
async function exportRiskMapsAsZip(): Promise<void> {
  const [riskBlob, residualBlob] = await Promise.all([
    exportRiskMapAsPNG(),
    exportResidualRiskMapAsPNG()
  ]);
  if (!riskBlob || !residualBlob) return;
  const zip = new JSZip();
  zip.file('risk-matrix.png', riskBlob);
  zip.file('residual-risk-matrix.png', residualBlob);
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const link = document.createElement('a');
  link.download = 'risk-maps.zip';
  link.href = URL.createObjectURL(zipBlob);
  link.click();
  URL.revokeObjectURL(link.href);
}

// Functions for editing risks
const startEdit = (id: string, field: RiskField) => {
  riskStore.startEdit(id, field);
  const risk = risks.value.find(r => r.id === id);
  editingValue.value = risk && field in risk ? String(risk[field as keyof Risk] ?? '') : '';
  nextTick(() => {
    const editInput = document.querySelector('.edit-select, .edit-input') as HTMLSelectElement | HTMLInputElement;
    if (editInput) editInput.focus();
  });
};

const saveEdit = async (id: string, field: RiskField) => {
  await riskStore.updateRiskField(id, field, editingValue.value);
};

const cancelEdit = () => {
  riskStore.stopEdit()
}

const deleteRisk = (id: string) => {
  riskStore.deleteRisk(id)
}

const addRisk = async () => {
  await riskStore.addDefaultRisk()
}

const handleImportRisks = async (risks: Omit<Risk, 'id'>[]) => {
  try {
    await riskStore.importRisks(risks);
    riskStore.closeImportWizard();
  } catch (error) {
    console.error('Failed to import risks:', error)
    throw error
  }
}
</script>

<style scoped>
.risk-dashboard {
  background-color: #131827;
  color: white;
  min-height: 100vh;
  padding: 20px;
  font-family: Arial, sans-serif;
  overflow-y: auto;
  overflow-x: hidden;
}

.export-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.button-group {
  display: flex;
  gap: 12px;
}

.dashboard-title {
  text-align: center;
  margin-bottom: 30px;
  font-size: 24px;
  font-weight: bold;
}

.dashboard-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding-bottom: calc(30vh + 20px);
}

.matrices-container {
  display: flex;
  gap: 40px;
}

.table-section {
  background-color: #333;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 30vh;
  box-sizing: border-box;
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
  border-top: 1px solid #444;
}

.table-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background-color: #2a2a2a;
  border-bottom: 1px solid #444;
  flex-shrink: 0;
}

.table-section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #f1f5f9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-section-drag-handle {
  color: #9ca3af;
  font-size: 18px;
  cursor: grab;
  user-select: none;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.table-section-drag-handle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.table-section-drag-handle:active {
  cursor: grabbing;
}

.risk-section {
  flex: 1;
  background-color: #333;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.risk-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.section-title {
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: 700;
  color: #f1f5f9;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title::after {
  content: '';
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #60a5fa, transparent);
  margin-left: 12px;
}

.risk-matrix {
  margin-bottom: 30px;
}

.matrix-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.matrix-label {
  width: 80px;
  font-weight: bold;
}

.matrix-labels {
  display: flex;
  flex: 1;
  gap: 10px;
}

.matrix-labels span {
  flex: 1;
  text-align: center;
  font-weight: bold;
}

.matrix-grid {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.matrix-row {
  display: flex;
  gap: 2px;
  height: 60px;
}

.severity-label {
  width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.matrix-cell {
  flex: 1;
  border: 1px solid #555;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 11px;
  font-weight: bold;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
}

.matrix-cell:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  z-index: 5;
}

.low-risk {
  background-color: #20b2aa;
}

.medium-risk {
  background-color: #ff8c00;
}

.high-risk {
  background-color: #dc143c;
}

.risk-markers {
  position: absolute;
  top: 2px;
  left: 2px;
  font-size: 10px;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.5);
  font-weight: 700;
  padding: 2px 4px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.matrix-footer {
  margin-top: 10px;
  display: flex;
  align-items: center;
}

.risk-table {
  background-color: #444;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Custom scrollbar for table */
.risk-table::-webkit-scrollbar {
  width: 8px;
}

.risk-table::-webkit-scrollbar-track {
  background: #333;
  border-radius: 4px;
}

.risk-table::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 4px;
}

.risk-table::-webkit-scrollbar-thumb:hover {
  background: #777;
}

.risk-table table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
}

.risk-table th,
.risk-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #555;
}

.risk-table tbody tr {
  transition: background-color 0.15s ease;
}

.risk-table tbody tr:hover {
  background-color: #3a3a3a;
  cursor: pointer;
}

.risk-table th {
  background-color: #555;
  font-size: 11px;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 10px;
  color: #e0e0e0;
}

.color-indicator {
  width: 20px;
  height: 20px;
  border-radius: 3px;
}

.color-low {
  background-color: #20b2aa;
}

.color-medium {
  background-color: #ff8c00;
}

.color-high {
  background-color: #dc143c;
}

.risk-description {
  max-width: 300px;
  min-width: 200px;
  word-wrap: break-word;
  line-height: 1.4;
}

.risk-id {
  font-family: monospace;
  font-size: 12px;
  color: #ccc;
  min-width: 80px;
}

.severity-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
  color: #ff6b6b;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  color: #ff4444;
  background-color: rgba(255, 107, 107, 0.1);
  transform: scale(1.1);
}

.delete-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.3);
}

.add-risk-btn {
  background-color: #4caf50;
  font-size: 11px;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  width: auto;
  min-width: 120px;
  display: block;
  margin: 12px auto 0;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
}

.add-risk-btn:hover {
  background-color: #45a049;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.4);
}

.add-risk-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(76, 175, 80, 0.3);
}

.add-risk-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.3);
}

/* New styles for editable cells */
.editable-cell {
  cursor: pointer;
  font-size: 11px;
  padding: 2px 2px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.editable-cell:hover {
  background-color: rgba(85, 85, 85, 0.6);
}

.editable-cell:focus {
  outline: 2px solid #4caf50;
  outline-offset: 2px;
}

.editing-cell {
  display: inline-block;
}

.edit-select {
  background-color: #333;
  color: white;
  border: 1px solid #666;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  min-width: 60px;
}

.edit-select:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.edit-select option {
  background-color: #333;
  color: white;
}

.edit-input {
  background-color: #333;
  color: white;
  border: 1px solid #666;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  min-width: 150px;
}

.edit-input:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

/* Hide ID column - useful for API calls but not for users */
.hidden-column {
  display: none;
}

/* Color Legend */
.legend {
  display: flex;
  gap: 24px;
  margin: 0 auto 30px;
  padding: 16px 24px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #cbd5e1;
}

.legend-color {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

/* Empty State */
.empty-state-row {
  pointer-events: none;
}

.empty-state {
  padding: 40px 20px !important;
  text-align: center;
  border: none !important;
}

.empty-state-content {
  color: #9ca3af;
}

.empty-state-content p {
  margin: 8px 0;
  font-size: 14px;
}

.empty-state-hint {
  font-size: 12px !important;
  color: #6b7280;
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .matrices-container {
    flex-direction: column;
    gap: 20px;
  }
  
  .risk-section {
    width: 100%;
  }
  
  .table-section {
    height: 30vh;
  }
  
  .dashboard-container {
    padding-bottom: calc(30vh + 20px);
  }
}

@media (max-width: 768px) {
  .risk-dashboard {
    padding: 12px;
  }
  
  .dashboard-container {
    gap: 20px;
    padding-bottom: calc(35vh + 20px);
  }
  
  .risk-section {
    padding: 16px;
  }
  
  .table-section {
    height: 35vh;
  }
  
  .risk-table {
    padding: 8px 12px;
  }
  
  .risk-table th,
  .risk-table td {
    padding: 8px 6px;
    font-size: 10px;
  }
  
  .legend {
    gap: 12px;
    padding: 8px;
  }
  
  .legend-item {
    font-size: 11px;
  }
  
  .legend-color {
    width: 20px;
    height: 20px;
  }
}
</style>
